import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Bell,
  Calendar,
  Check,
  Home,
  Shield,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'appointment': return Calendar;
      case 'home_care': return Home;
      case 'validation': return Shield;
      default: return Bell;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'appointment': return 'bg-red-100 text-red-500';
      case 'home_care': return 'bg-amber-100 text-amber-600';
      case 'validation': return 'bg-purple-100 text-purple-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="notifications-page">
      <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>
        Notifications
      </h1>

      {notifications.length > 0 ? (
        <div className="space-y-3">
          {notifications.map((notification) => {
            const Icon = getTypeIcon(notification.type);
            return (
              <Card
                key={notification.id}
                className={`border-0 shadow-sm cursor-pointer transition-all ${
                  !notification.is_read ? 'bg-red-50/50 border-l-4 border-l-teal-500' : ''
                }`}
                onClick={() => !notification.is_read && markAsRead(notification.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(notification.type)}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className={`font-semibold ${!notification.is_read ? 'text-slate-900' : 'text-slate-700'}`}>
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <Badge className="bg-red-500 text-white text-xs">Nouveau</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{notification.message}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucune notification</h3>
            <p className="text-slate-500 mt-2">Vous recevrez des notifications pour vos rendez-vous</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Notifications;
